/**
 * SyntheticAttributesDialog.jsx
 *
 * Admin-only dialog (gated additionally by a per-client feature flag).
 * Generates inferred psychological / socioeconomic / work attributes for a
 * population's individuals using AI. Stored separately from uploaded data.
 */
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Loader2, Sparkles, AlertTriangle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const WARNING_COPY =
  'Synthetic attributes are inferred estimates generated for simulation purposes only. They are not factual employee data and must not be used for individual-level decisions.';

export default function SyntheticAttributesDialog({ open, onOpenChange, population }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [limit, setLimit] = useState(100);

  const fetchStatus = async () => {
    if (!population?.id) return;
    setLoading(true);
    try {
      const res = await api.get(`/populations/${population.id}/synthetic-attributes`, { _silent: true });
      setStatus(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to load synthetic attribute status';
      toast.error(msg);
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, population?.id]);

  const handleGenerate = async () => {
    if (!population?.id) return;
    setGenerating(true);
    try {
      await api.post(`/populations/${population.id}/synthetic-expand?limit=${limit}`);
      toast.success(`Generation started for up to ${limit} individuals (runs in background).`);
      // Poll once after a short delay to refresh status
      setTimeout(fetchStatus, 4000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start generation');
    } finally {
      setGenerating(false);
    }
  };

  const handleClear = async () => {
    if (!population?.id) return;
    if (!window.confirm('Remove all synthetic attributes for this population?')) return;
    setClearing(true);
    try {
      const res = await api.delete(`/populations/${population.id}/synthetic-attributes`);
      toast.success(`Cleared ${res.data.modified} synthetic records`);
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clear synthetic attributes');
    } finally {
      setClearing(false);
    }
  };

  const enriched = status?.enriched_individuals ?? 0;
  const total = status?.total_individuals ?? 0;
  const coverage = total > 0 ? Math.round((enriched / total) * 100) : 0;
  const sample = status?.sample;
  const lastRun = status?.status;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="synthetic-attributes-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Sparkles className="w-4 h-4 text-blue-500" />
            AI Profile Enrichment
            <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-700 border-slate-200">Admin · Inferred</Badge>
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Enrich {population?.name || 'this population'} with AI-inferred psychological, socioeconomic and work
            attributes. Data is stored separately from uploaded fields and clearly labelled.
          </DialogDescription>
        </DialogHeader>

        <Alert className="border-amber-200 bg-amber-50" data-testid="synthetic-warning">
          <AlertTriangle className="h-4 w-4 text-amber-700" />
          <AlertTitle className="text-amber-800 text-sm font-semibold">Important — read before generating</AlertTitle>
          <AlertDescription className="text-xs text-amber-800/90 leading-relaxed">
            {WARNING_COPY}
          </AlertDescription>
        </Alert>

        {loading && (
          <div className="flex items-center gap-2 text-xs text-slate-500" data-testid="synthetic-loading">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Loading status...
          </div>
        )}

        {status && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3 text-center" data-testid="synthetic-coverage">
              <div className="rounded-md border border-slate-200 p-3">
                <p className="text-[10px] uppercase tracking-wide text-slate-400">Total individuals</p>
                <p className="text-xl font-semibold text-slate-800 tabular-nums">{total}</p>
              </div>
              <div className="rounded-md border border-slate-200 p-3">
                <p className="text-[10px] uppercase tracking-wide text-slate-400">Enriched</p>
                <p className="text-xl font-semibold text-slate-800 tabular-nums">{enriched}</p>
              </div>
              <div className="rounded-md border border-slate-200 p-3">
                <p className="text-[10px] uppercase tracking-wide text-slate-400">Coverage</p>
                <p className="text-xl font-semibold text-slate-800 tabular-nums">{coverage}%</p>
              </div>
            </div>

            {lastRun && (
              <div className="text-[11px] text-slate-500 border border-slate-100 rounded p-2 bg-slate-50" data-testid="synthetic-last-run">
                {lastRun.running ? (
                  <span>Generation started {new Date(lastRun.started_at).toLocaleString()} (limit {lastRun.limit}). Refresh in a moment to see progress.</span>
                ) : (
                  <span>
                    Last run: <strong>{lastRun.last_run_at ? new Date(lastRun.last_run_at).toLocaleString() : '—'}</strong>
                    {' · '}success: {lastRun.last_run_success ?? 0}, failed: {lastRun.last_run_failed ?? 0}
                  </span>
                )}
              </div>
            )}

            {sample?.synthetic_fields?.data && (
              <div className="space-y-2" data-testid="synthetic-sample">
                <p className="text-xs font-medium text-slate-700">Sample (one individual)</p>
                <div className="grid grid-cols-3 gap-3">
                  {Object.entries(sample.synthetic_fields.data).map(([category, fields]) => (
                    <div key={category} className="rounded border border-slate-100 p-2">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">{category}</p>
                      <div className="space-y-0.5">
                        {Object.entries(fields).map(([k, v]) => (
                          <div key={k} className="flex items-center justify-between text-[11px]">
                            <span className="text-slate-500 truncate">{k.replace(/_/g, ' ')}</span>
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-slate-50">{v}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-slate-400">Generated {sample.synthetic_fields.generated_at ? new Date(sample.synthetic_fields.generated_at).toLocaleString() : ''} · model {sample.synthetic_fields.model}</p>
              </div>
            )}

            <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
              <label className="text-xs text-slate-600">
                Generate up to&nbsp;
                <input
                  type="number"
                  className="w-16 border border-slate-200 rounded px-1.5 py-0.5 text-xs"
                  value={limit}
                  min={1}
                  max={1000}
                  onChange={(e) => setLimit(Math.max(1, Math.min(1000, parseInt(e.target.value || '0', 10))))}
                  data-testid="synthetic-limit-input"
                />
                &nbsp;individuals per run
              </label>
              <Button
                size="sm"
                onClick={handleGenerate}
                disabled={generating}
                className="ml-auto"
                data-testid="synthetic-generate-btn"
              >
                {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                {enriched > 0 ? 'Generate more' : 'Generate'}
              </Button>
              {enriched > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleClear}
                  disabled={clearing}
                  className="border-red-200 text-red-700 hover:bg-red-50"
                  data-testid="synthetic-clear-btn"
                >
                  {clearing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Trash2 className="w-3.5 h-3.5 mr-1.5" />}
                  Clear
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
